/**
 * PIN-TIME pass parsing (ADR-011 mechanism 1) — ALL expensive work happens
 * here, never at display time. Takes the file Laurie forwards (airline PDF,
 * iOS screenshot, photo of a printout) and produces:
 *
 *  - a raster (JPEG, long edge capped) that the Travel Card can paint
 *    instantly from IndexedDB, and
 *  - the decoded barcode payload + format, so the display path can re-render
 *    the code vectorially at max scanner contrast (and a future PKPass
 *    generator has exactly the bytes it needs — ADR-010/011).
 *
 * NOTE(INVARIANT): `pdfjs-dist` and `zxing-wasm` are loaded via await import()
 * ONLY — a static import would pull them into the core shell and blow the
 * <120KB gzip boot budget (ADR-000/ADR-011). A lint test greps this directory
 * to enforce it.
 *
 * NOTE(INVARIANT): pinFile never throws. A pass that fails to parse still
 * pins as a raster when one could be produced; when even that fails the
 * caller gets { ok: false, reason } — showing the picture (or an honest
 * message) always beats an exception at the gate.
 */

/** Long-edge cap for the stored raster: 2× a phone-width pass render is
 * plenty for the Travel Card, and barcode decode runs on the ORIGINAL image
 * bytes anyway (full resolution beats a downscale for PDF417 modules). */
export const RASTER_MAX_EDGE_PX = 1600;
const RASTER_JPEG_QUALITY = 0.92;

/** Barcode formats that appear on boarding passes (IATA BCBP = PDF417 for
 * print, Aztec/QR for mobile). Names are zxing-wasm's format identifiers. */
export const PASS_BARCODE_FORMATS = ["PDF417", "Aztec", "QRCode", "DataMatrix"] as const;
export type PassBarcodeFormat = (typeof PASS_BARCODE_FORMATS)[number];

export interface DecodedBarcode {
  /** Raw decoded payload (IATA BCBP string for real passes). */
  text: string;
  /** zxing format name ("PDF417" | "Aztec" | "QRCode" | ...). */
  format: string;
}

export type ParsedPass =
  | {
      ok: true;
      /** JPEG raster, long edge ≤ RASTER_MAX_EDGE_PX — the display bitmap. */
      raster: Blob;
      /** null when no barcode decoded — the raster still pins alone. */
      barcode: DecodedBarcode | null;
      source_name: string;
    }
  | { ok: false; reason: string; source_name: string };

function sourceName(file: File | Blob): string {
  return file instanceof File ? file.name : "";
}

/** PDF files start with "%PDF-" — sniff bytes, never trust MIME (iOS shares
 * PDFs as application/octet-stream often enough to matter). */
async function isPdf(file: Blob): Promise<boolean> {
  if (file.size < 5) return false;
  const head = new Uint8Array(await file.slice(0, 5).arrayBuffer());
  return head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46 && head[4] === 0x2d;
}

function capScale(width: number, height: number): number {
  const long = Math.max(width, height);
  return long > RASTER_MAX_EDGE_PX ? RASTER_MAX_EDGE_PX / long : 1;
}

function canvasToJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob !== null ? resolve(blob) : reject(new Error("canvas.toBlob returned null"))),
      "image/jpeg",
      RASTER_JPEG_QUALITY,
    );
  });
}

/** Render PDF page 1 to a capped JPEG raster. Lazy pdfjs chunk (ADR-011). */
async function rasterizePdf(file: Blob): Promise<Blob> {
  const pdfjs = await import("pdfjs-dist");
  // Vite serves the worker as its own hashed asset; ?url keeps it out of the
  // pass chunk too. Assigned idempotently — pdfjs reads it at getDocument.
  const worker = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;

  const task = pdfjs.getDocument({ data: await file.arrayBuffer() });
  try {
    const doc = await task.promise;
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 });
    // Never upscale a tiny PDF beyond 2×: pass PDFs are vector, 2× of the
    // declared page size already out-resolves any phone screen.
    const scale = Math.min(RASTER_MAX_EDGE_PX / Math.max(base.width, base.height), 2);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext("2d");
    if (ctx === null) throw new Error("2d canvas context unavailable");
    await page.render({ canvas, canvasContext: ctx, viewport }).promise;
    return await canvasToJpeg(canvas);
  } finally {
    void task.destroy().catch(() => undefined);
  }
}

/** Re-encode an image (screenshot/photo) to a capped JPEG raster. */
async function rasterizeImage(file: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = capScale(bitmap.width, bitmap.height);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    if (ctx === null) throw new Error("2d canvas context unavailable");
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return await canvasToJpeg(canvas);
  } finally {
    bitmap.close();
  }
}

/** Decode the pass barcode from an image blob. Lazy zxing chunk (ADR-011).
 * Failure is FINE (returns null) — never blocks a pin (ADR-011 mechanism 1). */
async function decodeBarcode(image: Blob): Promise<DecodedBarcode | null> {
  try {
    const { readBarcodes } = await import("zxing-wasm/reader");
    const results = await readBarcodes(image, {
      formats: [...PASS_BARCODE_FORMATS],
      tryHarder: true,
      maxNumberOfSymbols: 4, // some airlines print the code twice; take the first valid
    });
    for (const r of results) {
      if (r.isValid && r.text !== "") return { text: r.text, format: r.format };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Parse a boarding-pass file at pin time: raster + barcode payload.
 * PDFs decode from the rendered raster (there's no other bitmap); images
 * decode from the ORIGINAL bytes (full resolution), independent of the
 * stored raster's downscale.
 */
export async function pinFile(file: File | Blob): Promise<ParsedPass> {
  const source_name = sourceName(file);
  try {
    const pdf = await isPdf(file);
    const raster = pdf ? await rasterizePdf(file) : await rasterizeImage(file);
    const barcode = await decodeBarcode(pdf ? raster : file);
    return { ok: true, raster, barcode, source_name };
  } catch (err) {
    return { ok: false, reason: err instanceof Error ? err.message : String(err), source_name };
  }
}
